import { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { QrCode, WhatsappLogo, CheckCircle, WarningCircle, ArrowsClockwise } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function WhatsAppConnection() {
  const { user } = useAuth();
  const [status, setStatus] = useState("LOADING");
  const [detail, setDetail] = useState("");
  const [phase, setPhase] = useState("");
  const [qrCode, setQrCode] = useState(null);
  const [isResetting, setIsResetting] = useState(false);
  const statusRef = useRef(status);

  // Keep ref in sync so the interval callback always reads current status
  useEffect(() => { statusRef.current = status; }, [status]);

  const checkStatus = useCallback(async () => {
    try {
      const res = await api.get(`/whatsapp/status?userId=${user.id}`);
      setStatus(res.data.status || "DISCONNECTED");
      setDetail(res.data.detail || "");
      setPhase(res.data.phase || "");
      setQrCode(res.data.qr);
    } catch (e) {
      console.error(e);
      setStatus("ERROR");
    }
  }, [user?.id]);

  useEffect(() => {
    checkStatus();
    let lastPoll = Date.now();
    const interval = setInterval(() => {
      const now = Date.now();
      const pollInterval = statusRef.current === 'CONNECTED' ? 10000 : 4000;
      if (now - lastPoll >= pollInterval) {
        lastPoll = now;
        checkStatus();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [checkStatus]);

  const connect = async () => {
    try {
      await api.post(`/whatsapp/connect`, { userId: user.id });
      toast.success("Initializing WhatsApp connection...");
      checkStatus();
    } catch (e) {
      toast.error("Failed to connect to WhatsApp service");
    }
  };

  const reset = async (andReconnect = false) => {
    setIsResetting(true);
    try {
      await api.post(`/whatsapp/reset`, { userId: user.id });
      toast.success("WhatsApp session reset");
      if (andReconnect) {
        await api.post(`/whatsapp/connect`, { userId: user.id });
        toast.info("Starting fresh connection...");
      }
      checkStatus();
    } catch (e) {
      toast.error("Failed to reset WhatsApp session");
    } finally {
      setIsResetting(false);
    }
  };

  const logout = async () => {
    if (!window.confirm("Disconnect your WhatsApp?")) return;
    try {
      await api.post(`/whatsapp/logout`, { userId: user.id });
      toast.success("Disconnected WhatsApp");
      checkStatus();
    } catch (e) {
      toast.error("Failed to disconnect");
    }
  };

  return (
    <div className="border border-[#E5E7EB] bg-white p-6 lp-card-hover mt-6 lg:col-span-1">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <WhatsappLogo size={24} weight="bold" className="text-[#25D366]" />
          <h3 className="font-heading font-bold text-xl tracking-tight">WhatsApp Web</h3>
        </div>
        {status !== 'LOADING' && status !== 'DISCONNECTED' && (
          <button
            onClick={() => reset(false)}
            disabled={isResetting}
            title="Reset stuck session or locks"
            className="text-xs text-[#6B7280] hover:text-[#111827] flex items-center gap-1 font-semibold border border-[#E5E7EB] px-2 py-1 hover:bg-gray-50 transition-colors"
          >
            <ArrowsClockwise size={13} className={isResetting ? "animate-spin" : ""} />
            Reset
          </button>
        )}
      </div>

      {status === 'LOADING' && <p className="text-sm text-[#6B7280]">Checking status...</p>}

      {status === 'ERROR' && (
        <div>
          <div className="flex items-center gap-2 text-[#EF4444] text-sm font-bold mb-3">
            <WarningCircle size={18} weight="bold" /> WhatsApp service is unreachable
          </div>
          <button
            onClick={() => reset(true)}
            disabled={isResetting}
            className="bg-gray-100 hover:bg-gray-200 text-[#111827] text-xs font-bold px-3 py-1.5 flex items-center gap-1.5"
          >
            <ArrowsClockwise size={14} className={isResetting ? "animate-spin" : ""} />
            Retry / Reset Session
          </button>
        </div>
      )}

      {status === 'DISCONNECTED' && (
        <div>
          <p className="text-sm text-[#6B7280] mb-4">Connect your WhatsApp to send and automate messages directly from your number.</p>
          <button onClick={connect} className="bg-[#25D366] text-white font-bold px-5 py-2 hover:bg-[#1EBE5D] flex items-center gap-2">
            <QrCode size={18} weight="bold" /> Generate QR Code
          </button>
        </div>
      )}

      {(status === 'INITIALIZING' || status === 'RESTORING' || status === 'SYNCING') && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-[#25D366] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-[#111827] font-bold">
              {status === 'RESTORING'
                ? 'Restoring WhatsApp session...'
                : status === 'SYNCING'
                ? 'WhatsApp linked, syncing...'
                : 'Initializing WhatsApp Client...'}
            </p>
          </div>
          <p className={`text-xs ${phase === 'error' ? 'text-[#B45309]' : 'text-[#6B7280]'}`}>
            {detail || (status === 'RESTORING'
              ? 'Reconnecting your previously linked WhatsApp. No new QR scan needed.'
              : status === 'SYNCING'
              ? 'Phone is linked. Wait until this says Connected. Do not reset.'
              : 'Launching secure WhatsApp Web browser. QR appears in a few seconds — scan it as soon as it shows.')}
          </p>
          <div className="pt-1">
            <button
              onClick={() => reset(true)}
              disabled={isResetting}
              className="border border-[#D1D5DB] text-[#374151] hover:text-[#111827] hover:bg-gray-50 text-xs font-bold px-3 py-1.5 flex items-center gap-1.5 transition-colors"
            >
              <ArrowsClockwise size={14} className={isResetting ? "animate-spin" : ""} />
              {isResetting ? "Resetting..." : "Stuck? Reset & Force Reconnect"}
            </button>
          </div>
        </div>
      )}

      {status === 'QR_READY' && qrCode && (
        <div>
          <p className="text-sm font-bold text-[#111827] mb-2">Scan this QR code with your WhatsApp app:</p>
          <img src={qrCode} alt="WhatsApp QR Code" className="w-48 h-48 border border-[#E5E7EB] rounded-lg mb-2 p-2 bg-white" />
          <p className="text-xs text-[#6B7280] mb-3">Open WhatsApp on your phone {'>'} Linked Devices {'>'} Link a Device.</p>
          <button
            onClick={() => reset(true)}
            disabled={isResetting}
            className="text-xs text-[#002FA7] hover:underline flex items-center gap-1 font-semibold"
          >
            <ArrowsClockwise size={13} className={isResetting ? "animate-spin" : ""} />
            QR not scanning? Regenerate QR
          </button>
        </div>
      )}

      {status === 'CONNECTED' && (
        <div>
          <div className="flex items-center gap-2 text-[#10B981] font-bold text-sm mb-4">
            <CheckCircle size={20} weight="fill" /> WhatsApp Connected
          </div>
          <button onClick={logout} className="border border-[#EF4444] text-[#EF4444] font-bold px-4 py-1.5 text-xs hover:bg-red-50">
            Disconnect
          </button>
        </div>
      )}
    </div>
  );
}
